import React from 'react';
import { ArrowLeft, FileText, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';

export default function TermsOfService() {
  const navigate = useNavigate();
  const lastUpdated = "December 24, 2024";
  const companyName = "SafetyMind AI";
  const contactEmail = "support@safetymindai.com";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Terms of Service</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            Last updated: {lastUpdated}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Agreement */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Agreement to Terms</h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              By accessing or using {companyName} ("the Service"), you agree to be bound by these Terms of Service 
              ("Terms"). If you disagree with any part of these terms, you do not have permission to access the Service. 
              These Terms apply to all visitors, users, and others who access or use the Service.
            </p>
          </section>

          {/* Description of Service */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Description of Service</h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              {companyName} is a workplace safety management platform that provides tools for Job Safety Analysis (JSA), 
              safety audits, incident reporting, training management, and AI-powered safety recommendations. The Service 
              is designed to help organizations manage workplace safety compliance and documentation.
            </p>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">User Accounts</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">When you create an account with us, you must:</p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Provide accurate, complete, and current information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-300 mt-3">
              You may not use another person's account without permission. We reserve the right to refuse service, 
              terminate accounts, or remove content at our sole discretion.
            </p>
          </section>

          {/* Acceptable Use */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Acceptable Use</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">You agree not to use the Service to:</p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Violate any applicable laws or regulations</li>
              <li>Upload false, misleading, or fraudulent safety information</li>
              <li>Impersonate any person or entity</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Use the Service for any purpose other than workplace safety management</li>
              <li>Transmit viruses, malware, or other malicious code</li>
              <li>Harvest or collect user information without consent</li>
            </ul>
          </section>

          {/* Safety Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Safety Information Disclaimer</h2>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">Important Notice:</p>
              <p className="text-gray-600 dark:text-gray-300">
                The Service, including AI-generated safety recommendations, is provided as a tool to assist with 
                safety management. It does not replace professional safety expertise, regulatory compliance 
                requirements, or site-specific safety assessments. All safety information, hazard identifications, 
                and recommendations should be reviewed by qualified safety professionals before implementation. 
                {companyName} is not responsible for workplace incidents resulting from the use or misuse of 
                information provided through the Service.
              </p>
            </div>
          </section>

          {/* AI Features */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">AI-Powered Features</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Our Service includes AI-powered features for hazard identification, safety recommendations, and 
              document analysis. While we strive for accuracy, AI-generated content may contain errors or 
              omissions. You acknowledge that:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4 mt-3">
              <li>AI recommendations are suggestions, not definitive safety guidance</li>
              <li>Human review is required before implementing AI-generated content</li>
              <li>AI features may not account for all site-specific conditions</li>
              <li>You remain responsible for workplace safety decisions</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Intellectual Property</h2>
            <p className="text-gray-600 dark:text-gray-300">
              The Service and its original content, features, and functionality are owned by {companyName} and 
              are protected by international copyright, trademark, and other intellectual property laws. You 
              retain ownership of content you upload to the Service, but grant us a license to use, store, 
              and process that content to provide the Service.
            </p>
          </section>

          {/* Payment Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Subscription and Payment</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-3">
              Some features of the Service require a paid subscription. By subscribing, you agree to:
            </p>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 ml-4">
              <li>Pay all fees according to your selected plan</li>
              <li>Provide accurate payment information</li>
              <li>Subscriptions automatically renew unless cancelled</li>
              <li>Fees are non-refundable except as required by law</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-300 mt-3">
              We reserve the right to modify pricing with reasonable notice. Continued use after price changes 
              constitutes acceptance of new pricing.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Termination</h2>
            <p className="text-gray-600 dark:text-gray-300">
              We may terminate or suspend your account immediately, without prior notice, for any reason, 
              including breach of these Terms. Upon termination, your right to use the Service ceases 
              immediately. You may request export of your data prior to account termination, subject to 
              any legal retention requirements.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Limitation of Liability</h2>
            <p className="text-gray-600 dark:text-gray-300">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY 
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, 
              LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO 
              OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE.
            </p>
          </section>

          {/* Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Disclaimer</h2>
            <p className="text-gray-600 dark:text-gray-300">
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, 
              WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, 
              FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Governing Law</h2>
            <p className="text-gray-600 dark:text-gray-300">
              These Terms shall be governed by and construed in accordance with the laws of the United States 
              and the State of Ohio, without regard to its conflict of law provisions. Any disputes arising 
              from these Terms or your use of the Service shall be resolved in the courts located in Ohio.
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Changes to Terms</h2>
            <p className="text-gray-600 dark:text-gray-300">
              We reserve the right to modify these Terms at any time. We will notify you of material changes 
              by posting the new Terms on this page and updating the "Last updated" date. Your continued use 
              of the Service after changes are posted constitutes acceptance of the modified Terms.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="text-gray-700 dark:text-gray-200 font-medium">{companyName}</p>
              <p className="text-gray-600 dark:text-gray-300">
                Email: <a href={`mailto:${contactEmail}`} className="text-blue-600 hover:underline">{contactEmail}</a>
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                Phone: <a href="tel:4199443998" className="text-blue-600 hover:underline">419-944-3998</a>
              </p>
            </div>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 mt-8">
          © {new Date().getFullYear()} {companyName}. All rights reserved.
        </div>
      </div>
    </div>
  );
}
